import React, { lazy, Suspense, useEffect, type ReactNode, type ComponentType } from 'react'
import { NotFound, Route, Switch, useParams } from '@fs/zion-router'
import { AppLayout, colors, useContainerWidth } from '@fs/zion-ui'
import ErrorBoundary from '@fs/zion-error-boundary'
import { GroupsProvider, useGroupsContext, setLastViewedPid, setLastViewedPersonView } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { SWRConfig } from 'swr'
import { usePageViewEvent } from '@fs/zion-analytics'
import { pidRegexStr } from '@fs/zion-pid'
import { SelectSiblingPedigreeButton, SelectSiblingPedigreeOverlay } from '@fs/zion-tree-pedigree'
import { useFeatureFlag, useFeatureFlagWithPrefs } from '@fs/zion-flags'
import { usePermission } from '@fs/zion-permissions'
import { css } from '@emotion/core'
import RedirectToDetails from './helpers/RedirectToDetails'
import PersonAppProviders from './providers/PersonAppProviders'
import CenteredLoadingSpinner from './common/CenteredLoadingSpinner'
import useGroupOverlay from './helpers/useGroupOverlay'
import usePageDetailPrefix from './hooks/usePageDetailPrefix'
import AppLoading from './common/AppLoading'
import SubnavWrapper from './common/SubnavWrapper'
import ShowTempleProvider from './providers/ShowTempleContext'
import PageContentWidthContext, { sizes } from './person-page/PageContentWidthContext'
import { MERGE_ROUTE_PATH } from './merge/new-merge/mergeRouteUtil'
import { useContrastMode } from './ContrastModeProvider'
import ResearchHelpPageSkeleton from './research-help/ResearchHelpPageSkeleton'

const mainFlexCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`

const MergePage = lazy(() => import('./merge/MergePage'))
const UnmergePage = lazy(() => import('./unmerge/UnmergePage'))
const ChangeLogPage = lazy(() => import('./changelog/ChangeLog'))
const PersonPage = lazy(() => import('./person-page/PersonPage'))
const ResearchHelpPage = lazy(() => import('./research-help/ResearchHelpPage'))
const DevPage = lazy(() => import('./dev-page/DevPage'))
const PersonPrintViewPage = lazy(() => import('./person-print-view/PersonPrintView'))
const MergeAnalysisPage = lazy(() => import('./merge-analysis/MergeAnalysis'))
const SplitAnalysisPage = lazy(() => import('./split-analysis/SplitAnalysis'))
const QualityScoreSegmentsPage = lazy(() => import('./quality-score-segments/QualityScoreSegments'))

const subPages = [
  'details',
  'ordinances',
  'about',
  'sources',
  'memories',
  'vitals',
  'non-vitals',
  'family',
  'tools',
  'search-records',
  'collaborate',
  'timeline',
]

const PersonPageWrapper = (): JSX.Element => {
  useGroupOverlay()
  const { subPage, pid } = useParams()
  const { currentTreeId } = useGroupsContext()
  const sendPageView = usePageViewEvent()
  const pageDetailPrefix = usePageDetailPrefix()

  // page_detail updates link_page for all analytics past this point
  useEffect(() => {
    if (pageDetailPrefix !== undefined) {
      sendPageView({ page_detail: `${pageDetailPrefix}Person:${subPage}` })
    }
  }, [pageDetailPrefix, sendPageView, subPage])

  useEffect(() => {
    if (currentTreeId) {
      setLastViewedPid('')
    } else {
      setLastViewedPid(pid)
    }
  }, [pid, currentTreeId])

  useEffect(() => {
    subPage && setLastViewedPersonView(subPage)
  }, [subPage])

  return <PersonPage />
}

const DEV_PATH = '/'
const RESEARCH_HELP_PATH = `/research-help/:pid(${pidRegexStr})/:tab(dismissed)?`
const CHANGELOG_PATHS = [
  `/changelog/:pid(${pidRegexStr})/:relationshipType(parent-child|couple|association)/:relationshipId`,
  `/changelog/:pid(${pidRegexStr})/:filterType?`,
]
const MERGE_ANALYSIS_PATH = `/changelog/merge-analysis/:survivorPid(${pidRegexStr})/:duplicatePid(${pidRegexStr})`
const SPLIT_ANALYSIS_PATH = `/changelog/split-analysis/:combinedPid(${pidRegexStr})/:extractedPid(${pidRegexStr})`
const PERSON_PATH = `/:subPage(${subPages.join('|')})/:pid(${pidRegexStr})`
const REDIRECT_TO_DETAILS_PATHS = [`/:pid(${pidRegexStr})`, `/:subPage(${subPages.join('|')})`, '/']
const PRINT_VIEW_PATH = `/print-view/:pid(${pidRegexStr})/`
const UNMERGE_PATH = `/unmerge/:pid(${pidRegexStr})`

type GroupsLoadingGateProps = {
  children: ReactNode
  loader: ReactNode
}

/** Gates children on GroupsProvider loading state. Must be rendered inside GroupsProvider. */
const GroupsLoadingGate = ({ children, loader }: GroupsLoadingGateProps): JSX.Element => {
  const { loading } = useGroupsContext()
  if (loading) {
    return <>{loader}</>
  }
  return <>{children}</>
}

type EnhancedTreeRouteProps = {
  path: string | string[]
  backgroundColor?: string
  exact?: boolean
  LoadingComponent: ComponentType
  component?: ComponentType
  children?: ReactNode
  requirePersonalData?: boolean
} & ({ children: ReactNode } | { component: ComponentType })

/**
 * Wrapper around Route that applies the AppLayout and Suspense for lazy loaded components.
 */
const EnhancedTreeRoute = ({
  path,
  backgroundColor,
  exact,
  LoadingComponent,
  ...props
}: EnhancedTreeRouteProps): JSX.Element => (
  <Route path={path} exact={exact}>
    <AppLayout fullWidth backgroundColor={backgroundColor}>
      {/* Tree Route lazy loads ErrorPage without a suspense, so we need to wrap it in suspense here. Also used for the lazy loaded children. */}
      <Suspense fallback={<LoadingComponent />}>
        <TreeRoute {...props} LoadingComponent={LoadingComponent} />
      </Suspense>
    </AppLayout>
  </Route>
)

export default function App(): JSX.Element {
  const atWidth = useContainerWidth()
  const { isOn: advancedUnmergeIsOn } = useFeatureFlag('treeWeb_personR9_advancedUnmerge')
  const [canModifyPrivateSpaceEntities] = usePermission('ModifyAllPrivateSpaceEntitiesPermission')
  const newMergeAnalysisFlag = useFeatureFlagWithPrefs(
    'treeWeb_personR9_newMergeAnalysis',
    {},
    { userPreferences: ['fslabs.labs'] }
  )
  const newMergeFlag = useFeatureFlagWithPrefs(
    'treeWeb_personR9_newMerge2',
    {},
    { userPreferences: ['softLaunch.treeWeb_personR9_newMerge2', 'fslabs.labs'] }
  )
  const { isHighContrast } = useContrastMode()
  const backgroundColorForHighContrast = isHighContrast ? colors.gray05 : colors.gray03

  return (
    <SWRConfig value={{ revalidateOnFocus: process.env.NODE_ENV !== 'development' }}>
      <ErrorBoundary>
        <ShowTempleProvider>
          <PageContentWidthContext.Provider value={atWidth(sizes)}>
            <GroupsProvider
              noLoadingGating
              SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
              SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
            >
              <Switch>
                {/* /tree/person/print-view/ */}
                <EnhancedTreeRoute
                  path={PRINT_VIEW_PATH}
                  exact
                  backgroundColor={colors.gray00}
                  LoadingComponent={CenteredLoadingSpinner}
                >
                  <main id="skipToMainContent" css={mainFlexCss}>
                    <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                      <PersonAppProviders loadingComponent={<CenteredLoadingSpinner />}>
                        <Suspense fallback={<CenteredLoadingSpinner />}>
                          <PersonPrintViewPage />
                        </Suspense>
                      </PersonAppProviders>
                    </GroupsLoadingGate>
                  </main>
                </EnhancedTreeRoute>

                <Route path="*">
                  <SubnavWrapper />
                  <main id="skipToMainContent" css={mainFlexCss}>
                    <Switch>
                      {/* Checking NODE_ENV here guarantees that this will be stripped out in the production build. */}
                      {process.env.NODE_ENV === 'development' && (
                        <Route path={DEV_PATH} exact>
                          <AppLayout fullWidth backgroundColor={backgroundColorForHighContrast}>
                            <Suspense fallback={<CenteredLoadingSpinner />}>
                              <DevPage />
                            </Suspense>
                          </AppLayout>
                        </Route>
                      )}

                      {/* /tree/person/research-help/ */}
                      <EnhancedTreeRoute
                        path={RESEARCH_HELP_PATH}
                        exact
                        backgroundColor={colors.gray00}
                        LoadingComponent={ResearchHelpPageSkeleton}
                      >
                        <GroupsLoadingGate loader={<ResearchHelpPageSkeleton />}>
                          <PersonAppProviders loadingComponent={<ResearchHelpPageSkeleton />}>
                            <ResearchHelpPage />
                          </PersonAppProviders>
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/changelog/ */}
                      <EnhancedTreeRoute
                        path={CHANGELOG_PATHS}
                        exact
                        backgroundColor={colors.gray00}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <ChangeLogPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/changelog/merge-analysis/ */}
                      <EnhancedTreeRoute
                        path={MERGE_ANALYSIS_PATH}
                        exact
                        backgroundColor={newMergeAnalysisFlag.data?.isOn ? colors.gray05 : colors.gray00}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <MergeAnalysisPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/changelog/split-analysis/ */}
                      <EnhancedTreeRoute
                        path={SPLIT_ANALYSIS_PATH}
                        exact
                        backgroundColor={colors.gray00}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <SplitAnalysisPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /quality-score/segments */}
                      <EnhancedTreeRoute
                        path="/quality-score/segments"
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <QualityScoreSegmentsPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/merge/ */}
                      <EnhancedTreeRoute
                        path={MERGE_ROUTE_PATH}
                        exact
                        backgroundColor={
                          newMergeFlag.loading || newMergeFlag.data?.isOn ? colors.gray00 : colors.gray05
                        }
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <MergePage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/unmerge/ */}
                      {advancedUnmergeIsOn && canModifyPrivateSpaceEntities && (
                        <EnhancedTreeRoute
                          path={UNMERGE_PATH}
                          exact
                          backgroundColor={backgroundColorForHighContrast}
                          LoadingComponent={CenteredLoadingSpinner}
                        >
                          <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                            <UnmergePage />
                          </GroupsLoadingGate>
                        </EnhancedTreeRoute>
                      )}

                      {/* Person Subpages (details, ordinances, about, sources, etc.) */}
                      <EnhancedTreeRoute
                        path={PERSON_PATH}
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <PersonAppProviders loadingComponent={<CenteredLoadingSpinner />}>
                            <PersonPageWrapper />
                          </PersonAppProviders>
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* Redirect various paths to the person details view (or entered subpage) */}
                      <EnhancedTreeRoute LoadingComponent={AppLoading} exact path={REDIRECT_TO_DETAILS_PATHS}>
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <RedirectToDetails />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* 404 Not Found */}
                      <Route path="*">
                        <AppLayout fullWidth backgroundColor={backgroundColorForHighContrast}>
                          <NotFound />
                        </AppLayout>
                      </Route>
                    </Switch>
                  </main>
                </Route>
              </Switch>
            </GroupsProvider>
          </PageContentWidthContext.Provider>
        </ShowTempleProvider>
      </ErrorBoundary>
    </SWRConfig>
  )
}
